// ============================================================
// Summit League v2 — Create Team Page
//
// Focused team-creation form on game subdomains.
// Requires auth + game context. Shows game/season info header.
// Pre-checks if user already has a team for this game+season.
// ============================================================

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useGame } from '../hooks/useGame';
import { useToast } from '../hooks/useToast';
import axiosClient from '../api/axiosClient';

// UI
import Button from '../components/ui/Button';
import ToastContainer from '../components/ui/Toast';

// Styles
import '../styles/create-team.css';

// ── Constants ──────────────────────────────────────────────

const TEAM_NAME_MIN = 3;
const TEAM_NAME_MAX = 25;
const TEAM_TAG_MAX = 5;
const DESCRIPTION_MAX = 500;

// ── Validation Helpers ─────────────────────────────────────

function stripControlChars(raw: string): string {
  return raw
    .split('')
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('');
}

/** Normalize: strip control chars, collapse whitespace runs, trim */
function normalizeName(input: string): string {
  const clean = stripControlChars(input);
  return clean.replace(/\s+/g, ' ').trim();
}

interface ValidationResult {
  ok: boolean;
  message: string;
}

function validateTeamName(raw: string): ValidationResult {
  const name = normalizeName(raw);

  if (!name) return { ok: false, message: '' }; // empty — don't show error yet
  if (name.length < TEAM_NAME_MIN)
    return { ok: false, message: `Team name must be at least ${TEAM_NAME_MIN} characters.` };
  if (name.length > TEAM_NAME_MAX)
    return { ok: false, message: `Team name must be ${TEAM_NAME_MAX} characters or fewer.` };

  // Must start/end with alphanumeric. Middle: letters, numbers, spaces, hyphens, underscores.
  if (name.length >= 3) {
    const fullPattern = /^[A-Za-z0-9][A-Za-z0-9 _-]{1,23}[A-Za-z0-9]$/;
    if (!fullPattern.test(name)) {
      return {
        ok: false,
        message:
          'Letters, numbers, spaces, hyphens, and underscores only. Must start and end with a letter or number.',
      };
    }
  }

  return { ok: true, message: '' };
}

function validateTeamTag(raw: string): ValidationResult {
  const tag = stripControlChars(raw).trim();
  if (!tag) return { ok: true, message: '' }; // optional field
  if (tag.length > TEAM_TAG_MAX)
    return { ok: false, message: `Tag must be ${TEAM_TAG_MAX} characters or fewer.` };

  const pattern = /^[a-zA-Z0-9_-]+$/;
  if (!pattern.test(tag)) {
    return { ok: false, message: 'Letters, numbers, hyphens, and underscores only.' };
  }

  return { ok: true, message: '' };
}

// ── Types ──────────────────────────────────────────────────

interface ExistingTeam {
  team_id: number;
  team_name: string;
}

// ── Component ──────────────────────────────────────────────

const CreateTeamPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const { gameId, gameName, season, hasGame } = useGame();
  const toast = useToast();

  // Form state
  const [teamName, setTeamName] = useState('');
  const [teamTag, setTeamTag] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Blur tracking for inline validation
  const [nameTouched, setNameTouched] = useState(false);
  const [tagTouched, setTagTouched] = useState(false);

  // Pre-check: does user already have a team?
  const [existingTeam, setExistingTeam] = useState<ExistingTeam | null>(null);
  const [checkingTeam, setCheckingTeam] = useState(true);

  // ── Auth redirect (wait for auth to hydrate first) ───────

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // ── Pre-check for existing team ──────────────────────────

  useEffect(() => {
    if (authLoading || !isAuthenticated || !gameId) {
      setCheckingTeam(false);
      return;
    }

    let cancelled = false;

    const checkExistingTeam = async () => {
      try {
        setCheckingTeam(true);
        const res = await axiosClient.get(`/teams/me?game_id=${gameId}`);
        const teams = res.data?.data ?? res.data;

        if (!cancelled && Array.isArray(teams) && teams.length > 0) {
          // User already has a team for this game+season
          setExistingTeam({
            team_id: teams[0].team_id,
            team_name: teams[0].team_name,
          });
        }
      } catch {
        // 404 or no team — that's fine, let them create
      } finally {
        if (!cancelled) setCheckingTeam(false);
      }
    };

    checkExistingTeam();
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, gameId]);

  // ── Derived validation ───────────────────────────────────

  const nameNormalized = useMemo(() => normalizeName(teamName), [teamName]);
  const tagNormalized = useMemo(() => stripControlChars(teamTag).trim(), [teamTag]);

  const nameValidation = useMemo(() => validateTeamName(teamName), [teamName]);
  const tagValidation = useMemo(() => validateTeamTag(teamTag), [teamTag]);

  const canSubmit =
    nameNormalized.length >= TEAM_NAME_MIN &&
    nameValidation.ok &&
    tagValidation.ok &&
    !submitting;

  // ── Handlers ─────────────────────────────────────────────

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTeamName(stripControlChars(e.target.value));
  }, []);

  const handleTagChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTeamTag(stripControlChars(e.target.value));
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const clean = stripControlChars(e.target.value);
    if (clean.length <= DESCRIPTION_MAX) {
      setDescription(clean);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) return;

    try {
      setSubmitting(true);

      const body: Record<string, unknown> = {
        team_name: nameNormalized,
      };
      if (tagNormalized) body.team_tag = tagNormalized;
      if (description.trim()) body.description = description.trim();
      // Dev fallback: if no subdomain, pass game_id explicitly
      if (gameId) body.game_id = gameId;

      const res = await axiosClient.post('/teams', body);
      const data = res.data?.data ?? res.data;

      toast.success('Team Created!', `${data.team_name} is ready to compete.`);

      if (data.team_id) {
        navigate(`/teams/${data.team_id}`);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      const responseData = err?.response?.data;
      const status = err?.response?.status;
      const message =
        responseData?.error || responseData?.message || 'Unable to create team right now.';

      if (status === 409 && message.toLowerCase().includes('already on a team')) {
        toast.error('Already on a Team', 'You already have a team for this season.');
      } else if (status === 409) {
        toast.error('Name Taken', message);
      } else if (status === 429) {
        toast.error('Rate Limited', 'You can only create 2 teams per 6 hours. Please try again later.');
      } else {
        toast.error('Error', message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render guards ────────────────────────────────────────

  if (authLoading) return null; // Wait for auth to hydrate
  if (!isAuthenticated) return null;

  if (!hasGame) {
    return (
      <div className="sl-create-team">
        <div className="sl-create-team-inner">
          <div className="sl-card sl-create-team-card">
            <div className="sl-create-team-error-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--sl-warning)" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <h2 className="sl-create-team-error-title">No Game Selected</h2>
              <p className="sl-create-team-error-text">
                Visit a game subdomain to create a team. Choose your game from the homepage.
              </p>
              <Button variant="primary" size="md" onClick={() => navigate('/')}>
                Go to Homepage
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Loading state ────────────────────────────────────────

  if (checkingTeam) {
    return (
      <div className="sl-create-team">
        <div className="sl-create-team-inner">
          <div className="sl-card sl-create-team-card">
            <div className="sl-create-team-loading">
              <div className="sl-create-team-spinner" />
              <p className="sl-create-team-loading-text">Checking team status…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Already on a team ────────────────────────────────────

  if (existingTeam) {
    return (
      <div className="sl-create-team">
        <div className="sl-create-team-inner">
          <div className="sl-card sl-create-team-card">
            <div className="sl-create-team-error-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--sl-primary)" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <h2 className="sl-create-team-error-title">You're Already on a Team</h2>
              <p className="sl-create-team-error-text">
                You're a member of <strong>{existingTeam.team_name}</strong> for this season.
                You can only be on one team per game per season.
              </p>
              <div className="sl-create-team-actions">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => navigate(`/teams/${existingTeam.team_id}`)}
                >
                  View Your Team
                </Button>
                <Button variant="ghost" size="md" onClick={() => navigate('/')}>
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ────────────────────────────────────────────

  return (
    <div className="sl-create-team">
      <div className="sl-create-team-inner">
        {/* Context header */}
        <div className="sl-create-team-context">
          {gameName && (
            <span className="sl-create-team-context-game">{gameName}</span>
          )}
          {season && (
            <>
              <span className="sl-create-team-context-sep">•</span>
              <span className="sl-create-team-context-season">{season.seasonName}</span>
            </>
          )}
        </div>

        {/* Form card */}
        <div className="sl-card sl-create-team-card">
          <div className="sl-create-team-header">
            <h1 className="sl-create-team-title">Create a Team</h1>
            <p className="sl-create-team-subtitle">
              Build your squad for the current season and start competing.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="sl-create-team-form" noValidate>
            {/* Team Name */}
            <div className="sl-create-team-field">
              <label htmlFor="ct-name" className="sl-input-label">
                Team Name <span className="sl-create-team-required">*</span>
              </label>
              <input
                id="ct-name"
                type="text"
                className={`sl-input ${nameTouched && !nameValidation.ok && nameValidation.message ? 'sl-input--error' : ''}`}
                placeholder="Enter your team name"
                value={teamName}
                onChange={handleNameChange}
                onBlur={() => setNameTouched(true)}
                maxLength={TEAM_NAME_MAX + 5} // allow slight overflow for UX, validation catches it
                autoFocus
                autoComplete="off"
                disabled={submitting}
              />
              <div className="sl-create-team-field-meta">
                <span className="sl-input-helper">
                  {TEAM_NAME_MIN}–{TEAM_NAME_MAX} characters. Letters, numbers, spaces, hyphens, underscores.
                </span>
                <span className={`sl-create-team-char-count ${nameNormalized.length > TEAM_NAME_MAX ? 'sl-create-team-char-count--over' : ''}`}>
                  {nameNormalized.length}/{TEAM_NAME_MAX}
                </span>
              </div>
              {nameTouched && !nameValidation.ok && nameValidation.message && (
                <span className="sl-input-error-msg">{nameValidation.message}</span>
              )}
            </div>

            {/* Team Tag */}
            <div className="sl-create-team-field">
              <label htmlFor="ct-tag" className="sl-input-label">
                Team Tag
                <span className="sl-create-team-optional">optional</span>
              </label>
              <div className="sl-create-team-tag-wrapper">
                <input
                  id="ct-tag"
                  type="text"
                  className={`sl-input ${tagTouched && !tagValidation.ok ? 'sl-input--error' : ''}`}
                  placeholder="e.g. SLG"
                  value={teamTag}
                  onChange={handleTagChange}
                  onBlur={() => setTagTouched(true)}
                  maxLength={TEAM_TAG_MAX + 2}
                  autoComplete="off"
                  disabled={submitting}
                />
                {tagNormalized && (
                  <span className="sl-create-team-tag-preview">
                    [{tagNormalized.toUpperCase()}]
                  </span>
                )}
              </div>
              <div className="sl-create-team-field-meta">
                <span className="sl-input-helper">
                  1–{TEAM_TAG_MAX} characters. Letters, numbers, hyphens, underscores.
                </span>
                {tagNormalized.length > 0 && (
                  <span className={`sl-create-team-char-count ${tagNormalized.length > TEAM_TAG_MAX ? 'sl-create-team-char-count--over' : ''}`}>
                    {tagNormalized.length}/{TEAM_TAG_MAX}
                  </span>
                )}
              </div>
              {tagTouched && !tagValidation.ok && tagValidation.message && (
                <span className="sl-input-error-msg">{tagValidation.message}</span>
              )}
            </div>

            {/* Description */}
            <div className="sl-create-team-field">
              <label htmlFor="ct-desc" className="sl-input-label">
                Description
                <span className="sl-create-team-optional">optional</span>
              </label>
              <textarea
                id="ct-desc"
                className="sl-input sl-create-team-textarea"
                placeholder="Tell others about your team…"
                value={description}
                onChange={handleDescriptionChange}
                rows={3}
                disabled={submitting}
              />
              <div className="sl-create-team-field-meta">
                <span className="sl-input-helper" />
                <span className={`sl-create-team-char-count ${description.length > DESCRIPTION_MAX ? 'sl-create-team-char-count--over' : ''}`}>
                  {description.length}/{DESCRIPTION_MAX}
                </span>
              </div>
            </div>

            {/* Requirements info */}
            <div className="sl-create-team-info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--sl-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <div className="sl-create-team-info-text">
                <p>Teams require a minimum of 4 players and can have up to 6. You'll be the team captain and can invite players after creation.</p>
              </div>
            </div>

            {/* Actions */}
            <div className="sl-create-team-actions">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={!canSubmit}
                isLoading={submitting}
              >
                {submitting ? 'Creating Team…' : 'Create Team'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="lg"
                onClick={() => navigate('/')}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>

      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
};

export default CreateTeamPage;